import { FC, useEffect } from "react";
import { useNotificationSettingsDialogStore } from "@/stores/notification-settings-dialog.store";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Spinner,
  Switch,
} from "@capacit-com/lara-law-shared-ui";
import useNotificationSettings from "@/hooks/useNotificationSettings";
import type { NotificationSettingsType } from "@/services/notificationSettingsService";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { Permission } from "@/lib/permissions/Permission.enum";
import { logger } from "@/lib/logger";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import usePiiFeatureEnabled from "@/hooks/usePiiFeatureEnabled";

/**
 * Self-service notification subscriptions (LAR-27 follow-up). Toggles the
 * current user in/out of the tenant's per-type recipient lists. The
 * PII-override toggle is only shown to PII admins (and the API enforces the
 * same permission on subscribe) while the PII-Redaction flag is on; the
 * workspace row is always available.
 */

// Danish UI strings. TODO(LAR-27): migrate to the useContent/i18n content pack.
const TEXT = {
  title: "Notifikationer",
  description: "Vælg hvilke notifikationer du vil modtage.",
  workspaceLabel: "Workspace-opdateringer",
  workspaceDescription:
    "Få besked ved ændringer i workspaces og deres dokumenter.",
  piiLabel: "PII-tilsidesættelser",
  piiDescription:
    "Få besked, når en medarbejder tilsidesætter anonymisering af persondata i chatten.",
  error: "Kunne ikke opdatere notifikationsindstillingen. Prøv igen.",
  loadError: "Kunne ikke hente dine notifikationsindstillinger.",
  retry: "Prøv igen",
} as const;

/**
 * Mounted once in the router, beside the sidebar; the profile menu opens it
 * through the store. Mounting it inside the menu lost it on a resize across
 * the `md` breakpoint, where the sidebar swaps to a sheet.
 */
const NotificationSettingsDialog: FC = () => {
  const open = useNotificationSettingsDialogStore((state) => state.isOpen);
  const onOpenChange = useNotificationSettingsDialogStore(
    (state) => state.setOpen
  );
  const { useQuerySettings, setSubscription } = useNotificationSettings();
  const settingsQuery = useQuerySettings();
  const piiFeatureEnabled = usePiiFeatureEnabled();

  useEffect(() => {
    if (!settingsQuery.isError) return;
    logger.error("Failed to load notification settings", {
      operation: "getNotificationSettings",
      error: settingsQuery.error,
    });
  }, [settingsQuery.isError, settingsQuery.error]);

  const handleToggle = (
    settingsType: NotificationSettingsType,
    subscribed: boolean
  ) => {
    setSubscription.mutate(
      { settingsType, subscribed },
      {
        onError: (error) => {
          logger.error("Failed to update notification subscription", {
            operation: "setNotificationSubscription",
            error,
            settingsType,
            subscribed,
          });
          showErrorToast(TEXT.error);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{TEXT.title}</DialogTitle>
          <DialogDescription>{TEXT.description}</DialogDescription>
        </DialogHeader>

        {settingsQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : settingsQuery.isError && !settingsQuery.data ? (
          // A failed fetch with NO cached data must never render as
          // "everything unsubscribed". A failed refetch after a successful
          // load keeps the cached values instead — they are the last known
          // truth, and hiding working switches behind an error would be worse.
          <div className="flex flex-col items-start gap-3 py-2">
            <p className="text-sm text-muted-foreground">{TEXT.loadError}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void settingsQuery.refetch()}
            >
              {TEXT.retry}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-foreground">
                  {TEXT.workspaceLabel}
                </span>
                <span className="text-xs text-muted-foreground">
                  {TEXT.workspaceDescription}
                </span>
              </div>
              <Switch
                checked={settingsQuery.data?.workspaceSubscriber ?? false}
                disabled={setSubscription.isPending}
                onCheckedChange={(checked) =>
                  handleToggle("Workspace", checked === true)
                }
                aria-label={TEXT.workspaceLabel}
              />
            </div>

            {piiFeatureEnabled && (
              <PermissionGate allOf={[Permission.ManagePiiSettings]}>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium text-foreground">
                      {TEXT.piiLabel}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {TEXT.piiDescription}
                    </span>
                  </div>
                  <Switch
                    checked={settingsQuery.data?.piiOverrideSubscriber ?? false}
                    disabled={setSubscription.isPending}
                    onCheckedChange={(checked) =>
                      handleToggle("PiiOverride", checked === true)
                    }
                    aria-label={TEXT.piiLabel}
                  />
                </div>
              </PermissionGate>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NotificationSettingsDialog;
